import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationService } from '@app/api/notification.service';
import {
  NotificationStateService,
  UserNotification
} from '@app/services/notification-state.service';
import { SignalRService } from '@app/services/signalr.service';
import { AppState } from '@app/core/reducers';
import { currentUser } from '@app/core/auth';
import { User } from '@app/model/user';
import { environment } from '@app/../environments/environment';
import { ToastrService } from 'ngx-toastr';

type NotificationTab = 'unread' | 'all';

/** Roles allowed into the admin area — mirrors isAdminUser() used across the app. */
const ADMIN_ROLES = ['Admin', 'Content Admin', 'Salesman'];

/** JWT claim that carries the user's role(s). */
const JWT_ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

@Component({
  selector: 'meu-app-my-notifications',
  templateUrl: './app-my-notifications.component.html',
  styleUrls: ['./app-my-notifications.component.scss']
})
export class AppMyNotificationsComponent implements OnInit, OnDestroy {
  /** Currently selected tab. */
  activeTab: NotificationTab = 'unread';

  /** Notifications currently loaded for the active tab. */
  items: UserNotification[] = [];

  page = 1;
  pageSize = 20;
  total = 0;

  loading = false;
  loadingMore = false;
  markingAll = false;

  /** Cached current user, kept in sync from the store. */
  private user: User | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private notificationStateService: NotificationStateService,
    private signalRService: SignalRService,
    private store: Store<AppState>,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.store
      .pipe(select(currentUser), takeUntil(this.destroy$))
      .subscribe((u) => (this.user = u || null));

    this.loadPage(false);

    // Prepend notifications pushed in real time.
    this.signalRService.notificationReceived$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: any) => {
        if (!payload || !payload.id) {
          return;
        }
        // Avoid duplicates if the same notification is already in the list.
        if (this.items.some((n) => n.id === payload.id)) {
          return;
        }
        this.items = [payload as UserNotification, ...this.items];
        this.total += 1;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------- Tabs ----------------------

  changeTab(tab: NotificationTab): void {
    if (this.activeTab === tab || this.loading) {
      return;
    }
    this.activeTab = tab;
    this.page = 1;
    this.items = [];
    this.total = 0;
    this.loadPage(false);
  }

  // ---------------------- Data loading ----------------------

  loadPage(append: boolean): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    const isRead = this.activeTab === 'unread' ? false : undefined;

    this.notificationService
      .apiUserNotificationGet(this.page, this.pageSize, '-created_at', isRead)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          const data = res && res.data ? res.data : null;
          const collection: UserNotification[] = (data && data.collection) || [];

          this.items = append ? [...this.items, ...collection] : collection;
          this.total = (data && data.total) || 0;
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
        },
        () => {
          if (!append) {
            this.items = [];
            this.total = 0;
          }
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
        }
      );
  }

  get canLoadMore(): boolean {
    return !this.loading && !this.loadingMore && this.items.length < this.total;
  }

  loadMore(): void {
    if (!this.canLoadMore) {
      return;
    }
    this.page += 1;
    this.loadPage(true);
  }

  // ---------------------- Actions ----------------------

  /** Click a notification -> mark it as read, then go to its reference link. */
  onNotificationClick(notification: UserNotification): void {
    if (!notification.read) {
      this.notificationService.apiNotificationIdReadPut(notification.id).subscribe(
        (res) => {
          if (res && res.success) {
            notification.read = true;
            this.notificationStateService.markNotificationRead(notification.id);
            this.cdr.detectChanges();
          }
        },
        (err) => {
          console.error('[MyNotifications] markRead error', err);
        }
      );
    }

    this.goToReference(notification.reference_link);
  }

  /**
   * Navigate to the page pointed to by `reference_link`.
   * The backend stores a path relative to the FE origin (e.g. "/cart",
   * "/order/{id}/detail"); an absolute URL is opened in a new tab.
   *
   * Some notifications (mostly staff ones) point at admin routes. Navigating a
   * customer there switches to the admin layout and the route guard then blanks
   * the screen, so we only follow an admin link when the current user has an
   * admin role, and open it in a new tab.
   */
  private goToReference(link?: string): void {
    if (!link) {
      return;
    }
    const target = ('' + link).trim();
    if (!target) {
      return;
    }
    if (/^https?:\/\//i.test(target)) {
      window.open(target, '_blank');
      return;
    }
    const path = target.charAt(0) === '/' ? target : '/' + target;

    // "/admin/order/..." is a shared route that lives in the customer layout.
    const isStaffArea = /^\/admin(\/|$)/i.test(path) && !/^\/admin\/order\//i.test(path);
    if (!isStaffArea) {
      this.navigateTo(path);
      return;
    }

    // Staff-only area: switching the customer layout to the admin layout in-place
    // white-screens when the route guard denies. So only users with an admin role
    // may follow the link, and even then we open it in a new tab (fresh load) to
    // keep this page intact.
    if (this.isAdminUser()) {
      window.open(window.location.origin + path, '_blank');
    } else {
      this.toastr.info('Thông báo này liên kết tới khu vực dành cho quản trị viên.');
    }
  }

  /**
   * Role-based admin check, matching isAdminUser() used elsewhere in the app.
   * Prefers the roles on the store user, falling back to the JWT role claim
   * (the store user can be missing roles when it was loaded via the fallback path).
   */
  private isAdminUser(): boolean {
    const roles: string[] = [];

    if (this.user && Array.isArray(this.user.roles)) {
      roles.push(...(this.user.roles as string[]));
    }
    if (roles.length === 0) {
      roles.push(...this.getRolesFromToken());
    }

    return roles.some((role) => ADMIN_ROLES.indexOf(role) !== -1);
  }

  /** Decode role(s) from the JWT stored in localStorage. */
  private getRolesFromToken(): string[] {
    try {
      const token = localStorage.getItem(environment.authTokenKey);
      if (!token) {
        return [];
      }
      const parts = token.split('.');
      if (parts.length !== 3) {
        return [];
      }
      let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (payload.length % 4) {
        payload += '=';
      }
      const decoded = JSON.parse(atob(payload));
      const claim = decoded[JWT_ROLE_CLAIM];
      if (!claim) {
        return [];
      }
      return Array.isArray(claim) ? claim : [claim];
    } catch (e) {
      return [];
    }
  }

  private navigateTo(path: string): void {
    this.router.navigateByUrl(path).then(
      () => {},
      (err) => console.error('[MyNotifications] navigation error', err)
    );
  }

  markAllAsRead(): void {
    if (this.markingAll || this.items.length === 0) {
      return;
    }
    this.markingAll = true;

    this.notificationService.apiNotificationMarkAllAsReadPut().subscribe(
      (res) => {
        this.markingAll = false;
        if (res && res.success) {
          this.items.forEach((n) => (n.read = true));
          this.notificationStateService.markAllNotificationsRead();
          this.toastr.success('Đã đánh dấu tất cả thông báo là đã đọc');
          this.cdr.detectChanges();
        } else {
          this.toastr.error(res && res.message ? res.message : 'Đánh dấu thất bại');
        }
      },
      (err) => {
        this.markingAll = false;
        console.error('[MyNotifications] markAllAsRead error', err);
        this.toastr.error('Đánh dấu tất cả đã đọc thất bại');
      }
    );
  }

  // ---------------------- Display helpers ----------------------

  /** Bypass sanitizer so inline styles coming from the API render (bold / italic). */
  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  /** Format an ISO date to a localized "DD/MM/YYYY HH:mm" string. */
  formatDate(value?: string): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  }

  trackById(index: number, item: UserNotification): string {
    return item.id;
  }
}
